import { DatePicker, TimePicker } from "@mui/x-date-pickers";
import BaseCadastro from "components/BaseCadastro";
import { useToastContext } from "components/alert/ToastContext";
import BaseDataTableEdit from "components/dataTableableEdit/BaseDataTableEdit";
import { Label } from "components/input/Label";
import dayjs from "dayjs";
import Pessoa from "model/administracao/cadastro/Pessoa";
import Produto from "model/administracao/cadastro/produto/Produto";
import HorarioDia from "model/meta/producao/HorarioDia";
import MetaProducao from "model/meta/producao/MetaProducao";
import MetaProducaoHorario from "model/meta/producao/MetaProducaoHorario";
import MetaProducaoOperacao from "model/meta/producao/MetaProducaoOperacao";
import { AutoComplete } from "primereact/autocomplete";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { TabPanel, TabView } from "primereact/tabview";
import { useEffect, useState } from "react";
import "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import MetaProducaoService from "service/meta/producao/MetaProducaoService";
import { pessoaCompleteMethod, pessoaItemTemplate, produtoCompleteMethod, produtoItemTemplate } from "utils/autocompleteUtils";
import { generateKey } from "utils/generateKey";

const metaProducaoService = new MetaProducaoService();

export default function MetaProducaoCadastro() {

    const { msgAlerta } = useToastContext();

    const { control, clearErrors, setError, setFocus, register, formState: { errors } } = useForm();

    const [metaProducao, setMetaProducao] = useState<MetaProducao>(new MetaProducao());
    const [titulo, setTitulo] = useState<string>('Meta de Produção');

    const [produtos, setProdutos] = useState<Produto[]>([]);
    const [produtosFiltrados, setProdutosFiltrados] = useState<Produto[]>([]);

    const [clientes, setClientes] = useState<Pessoa[]>([]);
    const [clientesFiltrados, setClientesFiltrados] = useState<Pessoa[]>([]);

    const [horariosDia, setHorariosDia] = useState<HorarioDia[]>([]);

    const [activeIndex, setActiveIndex] = useState(0);

    function validar() {

        if (!metaProducao.data || metaProducao.data.toString() === "Invalid Date") {
            setError("data", { type: "manual", message: "Data é Obrigatória" }, { shouldFocus: true })
            return false;
        }
        if (!metaProducao.produto || !metaProducao.produto.codigo) {
            setError("produto", { type: "manual", message: "Produto é Obrigatório" }, { shouldFocus: true })
            return false;
        }
        if (!metaProducao.quantidade) {
            setError("quantidade", { type: "manual", message: "Quantidade é Obrigatória" }, { shouldFocus: true })
            return false;
        }
        if (metaProducao.quantidade < 0) {
            setError("quantidade", { type: "manual", message: "Quantidade deve ser maior que 0" }, { shouldFocus: true })
            return false;
        }
        if (!metaProducao.horarioDia) {
            setError("horarioDia", { type: "manual", message: "Horário é Obrigatório" }, { shouldFocus: true })
            return false;
        }
        return true;
    }

    function validarPersonalizado() {
        for (let i = 0; i < metaProducao.horarios.length; i++) {
            if (!metaProducao.horarios[i].horaInicio || !metaProducao.horarios[i].horaFim) {
                msgAlerta("Horário não informado", `Horários não foram informados corretamente no item ${i + 1}`);
                return false;
            }
            if (metaProducao.horarios[i].horaInicio >= metaProducao.horarios[i].horaFim) {
                msgAlerta("itens", "Hora início deve ser menor que hora fim");
                return false;
            }
            for (let j = 0; j < metaProducao.horarios.length; j++) {
                if (i == j) {
                    continue;
                }
                if (metaProducao.horarios[i].horaInicio >= metaProducao.horarios[j].horaInicio && metaProducao.horarios[i].horaInicio <= metaProducao.horarios[j].horaFim) {
                    msgAlerta("Horários não podem se sobrepor", `Horários dos itens ${j + 1} e ${i + 1} se sobrepõem`);
                    return false;
                }
            }
        }
        if (metaProducao.operacoes.length == 0) {
            msgAlerta("Operações", "Operações devem ser informadas");
            return false;
        }
        for (let i = 0; i < metaProducao.operacoes.length; i++) {
            if (metaProducao.operacoes[i].quantidadeOperadores <= 0) {
                msgAlerta("Operações", `Quantidade de operadores não informada para operação ${i + 1}`);
                setActiveIndex(2);
                return false;
            }
            if (metaProducao.operacoes[i].quantidadeOperadores > metaProducao.quantidade) {
                msgAlerta("Operações", "Quantidade de operadores não pode ser maior que a meta");
                return false;
            }
        }
        return true;
    }

    useEffect(() => {
        metaProducaoService.produtos().then((response) => {
            setProdutos(response);
        }).catch((err) => {
            console.error(err);
        });
        metaProducaoService.clientes().then((response) => {
            setClientes(response);
        }).catch((err) => {
            console.error(err);
        });
        metaProducaoService.horariosDia().then((response) => {
            setHorariosDia(response);
        }).catch((err) => {
            console.error(err);
        });
    }, []);

    const onChangeHorarioDia = (horario: HorarioDia) => {
        let horariosMeta: MetaProducaoHorario[] = [];

        for (let i = 0; i < horario.itens.length; i++) {
            let horarioMeta = new MetaProducaoHorario();
            horarioMeta.horaInicio = horario.itens[i].horaInicio;
            horarioMeta.horaFim = horario.itens[i].horaFim;
            horarioMeta.key = generateKey();
            horariosMeta.push(horarioMeta);
        }
        setMetaProducao({ ...metaProducao, horarioDia: horario, horarios: horariosMeta });
    }

    const onChangeProduto = (produto: Produto) => {
        let operacoesMeta: MetaProducaoOperacao[] = [];
        if (produto && produto.operacoes) {
            for (let i = 0; i < produto.operacoes.length; i++) {
                let operacaoMeta = new MetaProducaoOperacao();
                operacaoMeta.sequencial = produto.operacoes[i].sequencial;
                operacaoMeta.operacao = produto.operacoes[i].operacao;
                operacaoMeta.descricao = produto.operacoes[i].operacao?.descricao || "";
                operacaoMeta.qualidade = produto.operacoes[i].operacao?.qualidade || false;
                operacaoMeta.quantidadeOperadores = produto.operacoes[i].operacao?.quantidadeOperadores || 0;
                operacaoMeta.key = generateKey();
                operacoesMeta.push(operacaoMeta);
            }
        }

        setMetaProducao({ ...metaProducao, produto: produto, operacoes: operacoesMeta });
    }


    function timePicker(rowData: MetaProducaoHorario, field: string) {

        return <TimePicker
            value={dayjs().hour(0).minute(rowData[field as keyof MetaProducaoHorario] as number)}
            className="time-picker-horario"
            timeSteps={{ minutes: 1 }}
            onChange={(newValue) => {
                (rowData[field as keyof MetaProducaoHorario] as number) = newValue ? ((newValue.hour() * 60) + newValue.minute()) : 0;
                setMetaProducao({ ...metaProducao });
            }}
        />;
    }

    return (

        <BaseCadastro model={metaProducao} service={metaProducaoService} setModel={(value: MetaProducao) => setMetaProducao(value)}
            validar={() => validar()} validarPersonalizado={() => validarPersonalizado()}
            urlService="/metaProducao" pathCadastro="/metaProducao" tituloPagina={titulo}>

            {titulo ?
                <div className="row">
                    <label style={{ fontSize: 18, fontWeight: 'bold' }} className="col col-sm-12">{titulo}</label>
                </div>
                : null}

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                <TabPanel header="Dados gerais">
                    <div className="row">
                        <Label className="col col-sm-12" obrigatorio>Data</Label>
                        <div className="col-sm-12">
                            <Controller name="data" control={control} render={({ field }) => (
                                <DatePicker
                                    inputRef={field.ref}
                                    value={dayjs(metaProducao.data)}
                                    onChange={value => {
                                        clearErrors("data")
                                        setMetaProducao({ ...metaProducao, data: value?.toDate() || null })
                                    }}
                                />
                            )} />
                        </div>
                        {errors.data && errors.data.message ? <span className='col-sm-12' style={{ color: "red" }} >{errors.data?.message as string}</span> : null}
                    </div>
                    <div className="row">
                        <Label className="col col-sm-12" obrigatorio>Produto</Label>
                        <div className="col-sm-12">
                            <Controller name="produto" control={control} render={({ field }) => (
                                <AutoComplete
                                    inputRef={field.ref}
                                    field="nome"
                                    dropdown
                                    placeholder="Selecione um Produto"
                                    completeMethod={(e) => {
                                        let filtered = produtoCompleteMethod(produtos, e.query);
                                        setProdutosFiltrados(filtered)
                                    }}
                                    itemTemplate={produtoItemTemplate}
                                    suggestions={produtosFiltrados}
                                    value={metaProducao.produto}
                                    className={errors.produto ? "p-invalid" : ""}
                                    onChange={(e) => {
                                        clearErrors("produto")
                                        if (typeof e.target.value == "string") {
                                            let prod = new Produto();
                                            prod.nome = e.target.value;
                                            metaProducao.produto = prod;
                                            metaProducao.operacoes = [];
                                        } else {
                                            metaProducao.produto = e.target.value;

                                        }
                                        onChangeProduto(e.target.value as Produto);
                                    }}
                                    inputStyle={{ width: '100%' }}
                                    style={{ width: '30%', minWidth: 250 }}
                                />)}
                            />
                        </div>
                        {errors.produto && errors.produto.message ? <span className='col-sm-12' style={{ color: "red" }} >{errors.produto?.message as string}</span> : null}
                    </div>

                    <div className="row">
                        <Label className="col col-sm-12" obrigatorio>Quantidade</Label>
                        <div className="col-sm-12">
                            <Controller name="quantidade" control={control} render={({ field }) => (
                                <InputNumber required
                                    inputRef={field.ref}
                                    className={errors.quantidade ? "p-invalid" : ""}
                                    value={metaProducao.quantidade}
                                    onChange={(e) => {
                                        clearErrors("quantidade")
                                        setMetaProducao({ ...metaProducao, quantidade: e.value as number })
                                    }}
                                    style={{ width: '30%' }}
                                    locale="pt-BR"
                                    useGrouping={false}
                                    maxFractionDigits={0}
                                />
                            )} />
                        </div>
                        {errors.quantidade && errors.quantidade.message ? <span className='col-sm-12' style={{ color: "red" }} >{errors.quantidade?.message as string}</span> : null}
                    </div>

                    <div className="row">
                        <Label className="col col-sm-12">Total finalizado</Label>
                        <div className="col-sm-12">
                            <InputNumber
                                className={errors.quantidade ? "p-invalid" : ""}
                                value={metaProducao.operacoes && metaProducao.operacoes.length > 0 ? metaProducao.operacoes[metaProducao.operacoes.length - 1].apontamentos.reduce((acc, apontamento) => acc + apontamento.quantidade, 0) : 0}
                                disabled
                                style={{ width: '30%' }}
                                locale="pt-BR"
                                useGrouping={false}
                                maxFractionDigits={0}
                            />
                        </div>
                    </div>

                    <div className="row">
                        <Label className="col col-sm-12">Cliente</Label>
                        <div className="col-sm-12">
                            <Controller name="cliente" control={control} render={({ field }) => (
                                <AutoComplete
                                    inputRef={field.ref}
                                    dropdown
                                    value={metaProducao.cliente}
                                    placeholder="Selecione um Cliente"
                                    style={{ width: '30%', minWidth: 250 }}
                                    className={errors.cliente ? "p-invalid" : ""}
                                    field="nome"
                                    itemTemplate={(item: Pessoa) => pessoaItemTemplate(item)}
                                    suggestions={clientesFiltrados}
                                    completeMethod={(e) => {
                                        let filtered: Pessoa[] = pessoaCompleteMethod(clientes, e.query);
                                        setClientesFiltrados(filtered);
                                    }}
                                    onChange={(e) => {
                                        clearErrors("cliente")

                                        let metaProducaoLocal = JSON.parse(JSON.stringify(metaProducao));

                                        if (!e.target.value) {
                                            metaProducaoLocal.cliente = null;
                                        } else if (typeof e.target.value == "string") {
                                            let cliente = new Pessoa();
                                            cliente.nome = e.target.value;
                                            metaProducaoLocal.cliente = cliente;
                                        } else {
                                            metaProducaoLocal.cliente = e.target.value;
                                        }
                                        setMetaProducao(metaProducaoLocal);
                                    }}
                                />
                            )} />
                        </div>
                        {errors.cliente && errors.cliente.message ? <span className='col-sm-12' style={{ color: "red" }} >{errors.cliente?.message as string}</span> : null}
                    </div>

                    <div className="row">
                        <Label className="col col-sm-12" obrigatorio>Horário</Label>
                        <div className="col-sm-12">
                            <Controller name="horarioDia" control={control} render={({ field }) => (
                                <Dropdown
                                    focusInputRef={field.ref}
                                    value={metaProducao.horarioDia}
                                    placeholder="Selecione um Horário"
                                    style={{ width: '30%', minWidth: 250 }}
                                    className={errors.horarioDia ? "p-invalid" : ""}
                                    options={horariosDia}
                                    optionLabel="nome"
                                    dataKey="id"
                                    onChange={(e) => {
                                        clearErrors("horarioDia")
                                        onChangeHorarioDia(e.value as HorarioDia);
                                    }}
                                />
                            )} />
                        </div>
                        {errors.horarioDia && errors.horarioDia.message ? <span className='col-sm-12' style={{ color: "red" }} >{errors.horarioDia?.message as string}</span> : null}
                    </div>
                </TabPanel>
                <TabPanel header="Horários">
                    <div className="row" style={{ marginTop: 8 }}>
                        <div className="col-sm-6" style={{ padding: 0, marginTop: 8 }}>
                            <BaseDataTableEdit
                                tituloLista="Horários"
                                getNewModel={() => new MetaProducaoHorario()}
                                dataKey="key"
                                lista={metaProducao.horarios ? metaProducao.horarios : []}
                                setLista={(value: any) => setMetaProducao({ ...metaProducao, horarios: value })}
                                scrollable
                                scrollHeight='500px'
                                showEdit={false}

                            >
                                <Column field="" header="Hora início" style={{ width: '45%' }} body={(rowData) => timePicker(rowData, 'horaInicio')} />
                                <Column field="" header="Hora fim" style={{ width: '45%' }} body={(rowData) => timePicker(rowData, 'horaFim')} />
                            </BaseDataTableEdit>
                        </div>
                    </div>
                </TabPanel>
                <TabPanel header="Operações">
                    <div className="row" style={{ marginTop: 8 }}>
                        <div className="col-sm-12" style={{ padding: 0, marginTop: 8 }}>
                            <BaseDataTableEdit
                                tituloLista="Operações"
                                getNewModel={() => new MetaProducaoOperacao()}
                                dataKey="key"
                                lista={metaProducao.operacoes ? metaProducao.operacoes : []}
                                setLista={(value: any) => setMetaProducao({ ...metaProducao, operacoes: value })}
                                scrollable
                                scrollHeight='500px'
                                showEdit={false}
                                showDelete={false}
                                addButtonDisabled={true}

                            >
                                <Column field="descricao" header="Descrição" style={{ width: '40%' }} />
                                <Column field="quantidadeOperadores" header="Qtd Operadores" style={{ width: '15%' }}
                                    body={(rowData) => <InputNumber
                                        maxFractionDigits={3}
                                        locale='pt-BR' useGrouping={false}
                                        style={{ width: '100%' }} inputStyle={{ width: '100%' }}
                                        value={rowData.quantidadeOperadores}
                                        onChange={(e) => {
                                            rowData.quantidadeOperadores = e.value as number;
                                            setMetaProducao({ ...metaProducao });
                                        }} />}
                                />

                                <Column header="Meta/Dia" style={{ width: '15%' }} body={(rowData: MetaProducaoOperacao) => rowData.quantidadeOperadores ? Math.floor(metaProducao.quantidade / rowData.quantidadeOperadores) : ""} />
                                <Column header="Meta/Hora"
                                    style={{ width: '15%' }}
                                    body={(rowData: MetaProducaoOperacao) =>
                                        rowData.quantidadeOperadores && metaProducao.horarios.length > 0 ?
                                            Math.floor((metaProducao.quantidade / rowData.quantidadeOperadores) / metaProducao.horarios.length)
                                            : ""} />
                                <Column header="Total apontado"
                                    style={{ width: '20%' }}
                                    body={(rowData: MetaProducaoOperacao) => rowData.apontamentos ? rowData.apontamentos.reduce((acc, apontamento) => acc + apontamento.quantidade, 0) : 0}
                                />

                            </BaseDataTableEdit>
                        </div>
                    </div>
                </TabPanel>

            </TabView>

        </BaseCadastro >
    )
}
